import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Power, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const CLAIMS = [
  ['identity_verified', 'Identity verified'],
  ['full_name_verified', 'Full name verified'],
  ['email_verified', 'Email verified'],
  ['date_of_birth_verified', 'Date of birth verified'],
  ['age_over_16', 'Age over 16'],
  ['age_over_18', 'Age over 18'],
  ['age_over_21', 'Age over 21'],
  ['document_verified', 'Document verified'],
  ['selfie_match_verified', 'Selfie match verified'],
  ['identity_active', 'Identity active'],
  ['trusted_device_available', 'Trusted device available'],
  ['authentication_capable', 'Authentication capable'],
];

const LEVELS = [
  ['incomplete', 'Incomplete'],
  ['basic_candidate', 'Basic candidate'],
  ['substantial_candidate', 'Substantial candidate'],
  ['high_candidate', 'High candidate'],
];

const emptyForm = {
  name: '',
  description: '',
  environment: 'internal',
  allowed_claims: [],
  allowed_purposes: '',
  minimum_candidate_level: 'basic_candidate',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function ProfileStatus({ status }) {
  const tone = status === 'active'
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{status}</span>;
}

function levelLabel(value) {
  return LEVELS.find(([key]) => key === value)?.[1] || String(value || '—').replaceAll('_', ' ');
}

export default function RelyingPartyProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/idem-identities/admin/relying-party-profiles');
      setProfiles(data.profiles || []);
    } catch (requestError) {
      setError(requestError?.response?.status === 403
        ? 'This internal page is not available for this account.'
        : 'Relying Party Profiles could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingProfile(null);
    setForm(emptyForm);
    setPendingStatus(null);
    setNotice(null);
    setFormOpen(true);
  };

  const openEdit = (profile) => {
    setEditingProfile(profile);
    setForm({
      name: profile.name || '',
      description: profile.description || '',
      environment: profile.environment || 'internal',
      allowed_claims: profile.allowed_claims || [],
      allowed_purposes: (profile.allowed_purposes || []).join(', '),
      minimum_candidate_level: profile.minimum_candidate_level || 'basic_candidate',
    });
    setPendingStatus(null);
    setNotice(null);
    setFormOpen(true);
  };

  const toggleClaim = (claimKey) => {
    setForm((current) => ({
      ...current,
      allowed_claims: current.allowed_claims.includes(claimKey)
        ? current.allowed_claims.filter((key) => key !== claimKey)
        : [...current.allowed_claims, claimKey],
    }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    setNotice(null);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      environment: form.environment,
      allowed_claims: form.allowed_claims,
      allowed_purposes: form.allowed_purposes,
      minimum_candidate_level: form.minimum_candidate_level,
    };
    try {
      if (editingProfile) {
        await api.patch(`/idem-identities/admin/relying-party-profiles/${editingProfile.id}`, payload);
        setNotice({ tone: 'success', text: 'Internal relying party profile updated.' });
      } else {
        await api.post('/idem-identities/admin/relying-party-profiles', payload);
        setNotice({ tone: 'success', text: 'Internal relying party profile created.' });
      }
      setFormOpen(false);
      setEditingProfile(null);
      setForm(emptyForm);
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The relying party profile could not be saved.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitStatus = async (event) => {
    event.preventDefault();
    if (!pendingStatus || !statusReason.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const action = pendingStatus.action === 'disable' ? 'disable' : 'enable';
      await api.patch(`/idem-identities/admin/relying-party-profiles/${pendingStatus.profile.id}/${action}`, { reason: statusReason.trim() });
      setNotice({ tone: 'success', text: `Relying party profile ${action}d.` });
      setPendingStatus(null);
      setStatusReason('');
      await load();
    } catch (requestError) {
      setNotice({ tone: 'error', text: requestError?.response?.data?.error || 'The relying party profile status could not be updated.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="relying-party-profiles-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Relying Party Profiles" showDashboard={false}/>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Platform</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Relying Party Profiles</h1>
          <p className="mt-2 text-sm text-zinc-600">Internal consent configuration. Not a public integration.</p>
        </div>
        <button type="button" onClick={openCreate} className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-zinc-400">
          <Plus className="h-4 w-4" strokeWidth={1.5}/> Create profile
        </button>
      </div>

      {notice && <div className={`mb-4 border px-3 py-2 text-sm ${notice.tone === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>{notice.text}</div>}

      {formOpen && (
        <form onSubmit={submitForm} className="mb-6 border border-zinc-200 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">{editingProfile ? 'Edit internal relying party profile' : 'Create internal relying party profile'}</h2>
              <p className="mt-1 text-sm text-zinc-600">No credentials, redirect URIs, tokens, or external claim sharing are configured here.</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required maxLength={160} placeholder="Name"
              className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <select value={form.environment} onChange={(event) => setForm((current) => ({ ...current, environment: event.target.value }))} className="border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
              <option value="internal">Internal</option>
              <option value="sandbox">Sandbox</option>
              <option value="future_public">Future public</option>
            </select>
            <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} maxLength={500} rows={3} placeholder="Description (optional)"
              className="sm:col-span-2 resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <input value={form.allowed_purposes} onChange={(event) => setForm((current) => ({ ...current, allowed_purposes: event.target.value }))} maxLength={500} placeholder="Allowed purposes, comma separated (optional)"
              className="border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
            <select value={form.minimum_candidate_level} onChange={(event) => setForm((current) => ({ ...current, minimum_candidate_level: event.target.value }))} className="border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none">
              {LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <fieldset className="mt-4">
            <legend className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Allowed claims</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {CLAIMS.map(([claimKey, label]) => (
                <label key={claimKey} className="flex cursor-pointer items-center gap-2 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                  <input type="checkbox" checked={form.allowed_claims.includes(claimKey)} onChange={() => toggleClaim(claimKey)} className="h-3.5 w-3.5 border-zinc-300"/>
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => { setFormOpen(false); setEditingProfile(null); setForm(emptyForm); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!form.name.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">{editingProfile ? 'Save changes' : 'Create profile'}</button>
          </div>
        </form>
      )}

      {pendingStatus && (
        <form onSubmit={submitStatus} className="mb-6 border border-zinc-200 bg-white p-5">
          <h2 className="font-display text-lg font-semibold">{pendingStatus.action === 'disable' ? 'Disable' : 'Enable'} {pendingStatus.profile.name}</h2>
          <label className="mt-3 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reason</label>
          <textarea value={statusReason} onChange={(event) => setStatusReason(event.target.value)} required rows={3} maxLength={500}
            className="mt-1 w-full resize-y border border-zinc-200 px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"/>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setPendingStatus(null); setStatusReason(''); }} disabled={submitting} className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-400 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!statusReason.trim() || submitting} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Confirm</button>
          </div>
        </form>
      )}

      <div className="border border-zinc-200 bg-white">
        {loading ? <div className="px-4 py-8 text-sm text-zinc-500">Loading relying party profiles…</div>
          : error ? <div className="px-4 py-8 text-sm text-red-700">{error}</div>
            : profiles.length === 0 ? <div className="px-4 py-10 text-center text-sm text-zinc-500">No internal relying party profiles have been created yet.</div>
              : <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="border-b border-zinc-200 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500"><tr>
                    <th className="px-4 py-3 font-medium">RP ref</th><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Environment</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Allowed claims</th><th className="px-4 py-3 font-medium">Minimum candidate level</th><th className="px-4 py-3 font-medium">Created at</th><th className="px-4 py-3 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>{profiles.map((profile) => <tr key={profile.id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-600">{profile.rp_ref}</td>
                    <td className="px-4 py-4 align-middle"><div className="text-zinc-800">{profile.name}</div>{profile.description && <div className="mt-1 max-w-xs text-xs text-zinc-500">{profile.description}</div>}</td>
                    <td className="px-4 py-4 align-middle font-mono text-xs uppercase tracking-widest text-zinc-600">{profile.environment.replaceAll('_', ' ')}</td>
                    <td className="px-4 py-4 align-middle"><ProfileStatus status={profile.status}/></td>
                    <td className="px-4 py-4 align-middle text-zinc-600">{profile.allowed_claims?.length || 0}</td>
                    <td className="px-4 py-4 align-middle text-xs text-zinc-600">{levelLabel(profile.minimum_candidate_level)}</td>
                    <td className="whitespace-nowrap px-4 py-4 align-middle text-xs text-zinc-500">{formatDate(profile.created_at)}</td>
                    <td className="px-4 py-4 align-middle"><div className="flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => openEdit(profile)} className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400"><Pencil className="h-3 w-3" strokeWidth={1.5}/> Edit</button>
                      {profile.status === 'active' ? <button type="button" onClick={() => { setPendingStatus({ action: 'disable', profile }); setStatusReason(''); }} className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400"><Power className="h-3 w-3" strokeWidth={1.5}/> Disable</button>
                        : <button type="button" onClick={() => { setPendingStatus({ action: 'enable', profile }); setStatusReason(''); }} className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-700 hover:border-zinc-400"><RotateCcw className="h-3 w-3" strokeWidth={1.5}/> Enable</button>}
                    </div></td>
                  </tr>)}</tbody>
                </table>
              </div>}
      </div>
    </div>
  );
}
